import { getSessionId } from './lib/session';

const DIRECT_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

// On Cloudflare Pages the API is served from the site's own origin through the
// /api proxy (frontend/functions/api), so the session cookies are first-party.
// Safari and every iOS browser drop third-party cookies, which signed users
// out on each reload and made signed-in calls (AI planner) fail.
// REACT_APP_API_PROXY=on forces the proxy (custom domain on Pages), =off disables it.
export function resolveApiBase({
  direct = DIRECT_BASE,
  hostname = typeof window !== 'undefined' ? window.location.hostname : '',
  proxy = process.env.REACT_APP_API_PROXY,
  nodeEnv = process.env.NODE_ENV,
} = {}) {
  if (proxy === 'off') return direct;
  if (proxy === 'on') return '/api';
  return nodeEnv === 'production' && /(^|\.)pages\.dev$/i.test(hostname) ? '/api' : direct;
}

export const BASE = resolveApiBase();

function readCookie(name) {
  const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return m ? decodeURIComponent(m[1]) : null;
}

// The API sets gb_csrf on its own domain, which document.cookie can't read when the
// frontend is on another site, so the server also returns the token in auth responses.
const CSRF_KEY = 'gb_csrf_token';
const SESSION_HINT_KEY = 'gb_session_hint';

export function getCsrfToken() {
  const fromCookie = readCookie('gb_csrf');
  if (fromCookie) return fromCookie;
  try { return localStorage.getItem(CSRF_KEY); } catch { return null; }
}

export function setCsrfToken(token) {
  try {
    if (token) localStorage.setItem(CSRF_KEY, token);
    else localStorage.removeItem(CSRF_KEY);
  } catch { /* storage unavailable */ }
}

export function hasSessionHint() {
  if (readCookie('gb_logged_in')) return true;
  try { return localStorage.getItem(SESSION_HINT_KEY) === '1'; } catch { return false; }
}

export function setSessionHint(on) {
  try {
    if (on) localStorage.setItem(SESSION_HINT_KEY, '1');
    else localStorage.removeItem(SESSION_HINT_KEY);
  } catch { /* storage unavailable */ }
}

// Long enough to ride out a Render cold start; the place sheet shows its basics meanwhile.
const PLACE_TIMEOUT_MS = 20000;

// Calls that must not trigger a refresh-and-retry when they return 401.
const NO_REFRESH_PATHS = /^\/auth\/(refresh|login|signup|logout|verify-email|resend-verification|forgot-password|reset-password)\b|^\/oauth\//;
let refreshing = null;

// The access cookie lives 24h; the refresh cookie 30 days. Swap the refresh
// cookie for a new access cookie once, shared by every call that hit the 401.
export function refreshSession() {
  if (!refreshing) {
    refreshing = fetch(`${BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() || '' },
      credentials: 'include',
    })
      .then(async (res) => {
        if (!res.ok) return false;
        const data = await res.json().catch(() => ({}));
        if (data && typeof data.csrfToken === 'string') setCsrfToken(data.csrfToken);
        return true;
      })
      .catch(() => false)
      .finally(() => { setTimeout(() => { refreshing = null; }, 0); });
  }
  return refreshing;
}

async function request(path, options = {}, retried = false) {
  const { headers: customHeaders, timeoutMs, method: reqMethod = 'GET', ...restOptions } = options;
  const sessionId = getSessionId();
  // Opt-in deadline (covers headers and body) so a stalled call rejects instead of
  // leaving its caller loading forever.
  const ctrl = timeoutMs ? new AbortController() : null;
  const timer = ctrl ? setTimeout(() => ctrl.abort(), timeoutMs) : null;
  let res;
  let raw;
  try {
    res = await fetch(`${BASE}${path}`, {
      method: reqMethod,
      headers: {
        'Content-Type': 'application/json',
        ...(reqMethod !== 'GET' && reqMethod !== 'HEAD' ? { 'X-CSRF-Token': getCsrfToken() || '' } : {}),
        ...(sessionId ? { 'X-Session-Id': sessionId } : {}),
        ...customHeaders,
      },
      credentials: 'include',
      ...(ctrl ? { signal: ctrl.signal } : {}),
      ...restOptions,
    });
    // Some failures never produce JSON (413 payload too large, 502/504 from a proxy,
    // rate-limit HTML pages). Read the body defensively so the caller can show the
    // real reason instead of a JSON parse error.
    raw = await res.text();
  } catch (err) {
    if (ctrl && ctrl.signal.aborted) throw new Error('Request timed out');
    throw err;
  } finally {
    if (timer) clearTimeout(timer);
  }
  let data;
  try {
    data = raw ? JSON.parse(raw) : {};
  } catch {
    if (!res.ok) throw new Error(`Request failed (${res.status} ${res.statusText || 'error'})`);
    throw new Error('Server returned an unexpected response');
  }
  if (res.status === 401 && !retried && data.code !== 'SESSION_REVOKED' && !NO_REFRESH_PATHS.test(path) && hasSessionHint()) {
    if (await refreshSession()) return request(path, options, true);
  }
  if (!res.ok) {
    const e = new Error(data.error || `Request failed (${res.status})`);
    e.code = data.code;
    e.status = res.status;
    e.data = data;
    throw e;
  }
  if (data && typeof data.csrfToken === 'string') setCsrfToken(data.csrfToken);
  return data;
}

export const auth = {
  signup: (name, email, password, username) =>
    request('/auth/signup', { method:'POST', body:JSON.stringify({ name, email, password, username }) }),
  usernameAvailable: (u) => request(`/auth/username-available?u=${encodeURIComponent(u)}`),
  setUsername: (username) => request('/auth/username', { method: 'PUT', body: JSON.stringify({ username }) }),
  login: (email, password) =>
    request('/auth/login', { method:'POST', body:JSON.stringify({ email, password }) }),
  me: () => request('/auth/me'),
  updatePreferences: (prefs) =>
    request('/auth/preferences', { method: 'PATCH', body: JSON.stringify(prefs) }),
  logout: () => request('/auth/logout', { method:'POST' }),
  verifyEmail: (token) =>
    request('/auth/verify-email', { method:'POST', body:JSON.stringify({ token }) }),
  resendVerification: (email) =>
    request('/auth/resend-verification', { method:'POST', body:JSON.stringify({ email }) }),
  forgotPassword: (email) =>
    request('/auth/forgot-password', { method:'POST', body:JSON.stringify({ email }) }),
  resetPassword: (token, password) =>
    request('/auth/reset-password', { method:'POST', body:JSON.stringify({ token, password }) }),
};

export const oauth = {
  google: (token, type = 'id_token') =>
    request('/oauth/google', { method:'POST', body:JSON.stringify(
      type === 'access_token'
        ? { accessToken: token, tokenType: 'access_token' }
        : { idToken: token }
    )}),
};

export const destinations = {
  list:   (params = {}) => { const qs = new URLSearchParams(params).toString(); return request(`/destinations${qs?`?${qs}`:''}`); },
  get:    (id)   => request(`/destinations/${id}`),
  getContent: (id) => request(`/destinations/${id}/content`),
  // Real data behind the detail page: verified place counts, top sights, food spots,
  // lodging tiers, day trips and CityContent prose. Empty/null wherever we hold nothing.
  overview: (id) => request(`/destinations/${id}/overview`),
  save:   (id)   => request(`/destinations/${id}/save`, { method:'POST' }),
  unsave: (id)   => request(`/destinations/${id}/save`, { method:'DELETE' }),
  saved:  ()     => request('/destinations/saved/all'),
  discover: () => request('/destinations/discover'),
  // Both are served from our own city table now. `autocomplete` returns only
  // cities the planner can actually build a trip for; `search` returns every
  // city we hold unless plannable:true is passed, because Explore and global
  // search legitimately want the wider set.
  autocomplete: (query) => request(`/destinations/autocomplete?query=${encodeURIComponent(query)}`),
  search: (query, { plannable = false, all = false } = {}) => request(
    `/destinations/search?query=${encodeURIComponent(query)}${plannable ? '&plannable=1' : ''}${all ? '&limit=all' : ''}`,
  ),
  // Which preferences this destination can actually serve. Asked with the trip
  // shape, because the answer depends on how deep the planner will retrieve.
  preferences: (dest, { days = 3, transport = 'transit' } = {}) => {
    const q = new URLSearchParams({ days: String(days), transport });
    if (dest && (dest.cityId || dest.id)) q.set('cityId', String(dest.cityId || dest.id));
    else if (dest && dest.name) q.set('name', dest.name);
    return request(`/destinations/preferences?${q.toString()}`);
  },
  // Manual planner palette: resolve a chosen destination (from the shared search) to
  // our KB metro and return its top verified places. { city, places } — city null if
  // none. Pass lat/lng from the search result to skip a Google geocode round-trip.
  palette: ({ cityId, name, placeId, lat, lng, page } = {}) => {
    const qs = new URLSearchParams();
    if (cityId) qs.set('cityId', cityId);
    if (name) qs.set('name', name);
    if (placeId) qs.set('placeId', placeId);
    if (lat != null) qs.set('lat', lat);
    if (lng != null) qs.set('lng', lng);
    if (page != null) qs.set('page', page);
    return request(`/destinations/palette?${qs.toString()}`);
  },
  // Search a resolved city's whole metro by name — backs the planner's search when a
  // wanted place is outside the top-200 palette.
  paletteSearch: (cityId, q) => request(`/destinations/palette-search?cityId=${cityId}&q=${encodeURIComponent(q)}`),
  // Day trips whose home is this city. plannable entries carry the target city
  // (id/name/coords) so the planner can load its palette; info-only ones are text.
  // One verified place + its Wikipedia lead ({ place, city, about }) for the place sheet.
  place: (placeId) => request(`/destinations/places/${encodeURIComponent(placeId)}`, { timeoutMs: PLACE_TIMEOUT_MS }),
  daytrips: (homeCityId) => request(`/destinations/daytrips?homeCityId=${homeCityId}`),
  // Party-specific trip cost from CityCost. `budget` on a destination is one traveller's
  // Comfort per-person-per-day, which ranks cities well but cannot answer "what will four of
  // us pay" -- four people book two rooms, not four. The party arithmetic lives server-side
  // in lib/tripBudget. POSTs because the inputs describe a trip, not a resource.
  estimate: (params) => request('/destinations/estimate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  }),
};

export const trips = {
  list:   ()         => request('/trips'),
  get:    (id)       => request(`/trips/${id}`),
  getBySlug: (slug)  => request(`/trips/slug/${encodeURIComponent(slug)}/public`),
  trash:  ()         => request('/trips/trash'),
  restore: (id)      => request(`/trips/${id}/restore`, { method:'POST' }),
  deleteForever: (id) => request(`/trips/${id}?permanent=true`, { method:'DELETE' }),
  clone:  (id, data) => request(`/trips/${id}/clone`, { method:'POST', body:JSON.stringify(data) }),
  create: (data)     => request('/trips', { method:'POST', body:JSON.stringify(data) }),
  update: (id, data) => request(`/trips/${id}`, { method:'PUT', body:JSON.stringify(data) }),
  delete: (id)       => request(`/trips/${id}`, { method:'DELETE' }),
  cancel: (id)       => request(`/trips/${id}/cancel`, { method:'POST' }),
  uncancel: (id)     => request(`/trips/${id}/uncancel`, { method:'POST' }),
  getLogs: (id)      => request(`/trips/${id}/logs`),
  saveLog: (id, data) => request(`/trips/${id}/logs`, { method:'POST', body:JSON.stringify(data) }),
  deleteLog: (id, dayNumber) => request(`/trips/${id}/logs/${dayNumber}`, { method:'DELETE' }),
  share:  (id)       => request(`/trips/${id}/share`, { method:'POST' }),
  // Manual planner feasibility check: per-day travel/visit totals + warnings.
  planCheck: (payload) => request('/trips/plan-check', { method:'POST', body:JSON.stringify(payload) }),
};

export const experiences = {
  list:    (tripId)        => request(`/experiences/${tripId}`),
  create:  (data)          => request('/experiences', { method:'POST', body:JSON.stringify(data) }),
  update:  (id, data)      => request(`/experiences/${id}`, { method:'PUT', body:JSON.stringify(data) }),
  delete:  (id)            => request(`/experiences/${id}`, { method:'DELETE' }),
  getUploadUrl: (id, contentType, filename) =>
    request(`/experiences/${id}/photos`, { method:'POST', body:JSON.stringify({ contentType, filename }) }),
  confirmPhoto: (id, photoId, key, caption, order) =>
    request(`/experiences/${id}/photos/${photoId}`, { method:'PUT', body:JSON.stringify({ key, caption, order }) }),
  deletePhoto: (id, photoId) =>
    request(`/experiences/${id}/photos/${photoId}`, { method:'DELETE' }),
};

export const blogs = {
  list:   ()         => request('/blogs'),
  get:    (tripId)   => request(`/blogs/${tripId}`),
  create: (data)     => request('/blogs', { method:'POST', body:JSON.stringify(data) }),
  update: (tripId, data) => request(`/blogs/${tripId}`, { method:'PUT', body:JSON.stringify(data) }),
  getPublic: (tripId) => request(`/blogs/public/${tripId}`),
};

export const ai = {
  generateItinerary: (params) =>
    request('/ai/itinerary', { method:'POST', body:JSON.stringify(params) }),
  refine: (currentItinerary, instruction, { transport, style, pace, travelers, travelersLabel, budgetMult } = {}) =>
    request('/ai/refine', { method:'POST', body:JSON.stringify({ currentItinerary, instruction, transport, style, pace, travelers, travelersLabel, budgetMult }) }),
  chat: (message, context) =>
    request('/ai/chat', { method:'POST', body:JSON.stringify({ message, context }) }),
  generateBlog: (tripId) =>
    request('/ai/blog', { method:'POST', body:JSON.stringify({ tripId }) }),
  // Deterministic day-trip repack: vacate one day of the plan, fill it with the
  // target city's real places and re-pack the vacated day's stops into the rest.
  daytripSwap: (payload) =>
    request('/ai/daytrip-swap', { method: 'POST', body: JSON.stringify(payload) }),
};

export const guides = {
  list: () => request('/guides'),
  get: (slug) => request(`/guides/${encodeURIComponent(slug)}`),
};

// Promos
export const promos = {
  list:   (params = {}) => { const qs = new URLSearchParams(params).toString(); return request(`/promos${qs ? `?${qs}` : ''}`); },
  get:    (id)          => request(`/promos/${id}`),
  view:   (id)          => request(`/promos/${id}/view`, { method: 'POST', body: JSON.stringify({}) }),
  create: (data)        => request('/promos', { method: 'POST', body: JSON.stringify(data) }),
  delete: (id)          => request(`/promos/${id}`, { method: 'DELETE' }),
  archive: (id)         => request(`/promos/${id}/archive`, { method: 'POST' }),
  unarchive: (id)       => request(`/promos/${id}/unarchive`, { method: 'POST' }),
  extend: (id, expireAfterDays) =>
    request(`/promos/${id}/extend`, { method: 'POST', body: JSON.stringify({ expireAfterDays }) }),
  like:   (id)          => request(`/promos/${id}/like`, { method: 'POST' }),
  comments: (id)        => request(`/promos/${id}/comments`),
  addComment: (id, body) =>
    request(`/promos/${id}/comments`, { method: 'POST', body: JSON.stringify({ body }) }),
  mine:   ()            => request('/promos/my/posts'),
  getUploadUrl: (contentType, size) => request('/promos/upload-url', { method: 'POST', body: JSON.stringify({ contentType, size }) }),
  getVariants: (id)     => request(`/promos/${id}/variants`),
  trackShare: (id, platform) => request(`/promos/${id}/share`, { method: 'POST', body: JSON.stringify({ platform }) }),
  trackShareStructured: (id, platform) => request(`/promos/${id}/shares`, { method: 'POST', body: JSON.stringify({ platform }) }),
  getPlatformStats: (id) => request(`/analytics/promos/${id}/platform-stats`),
  getClickUrl: (id, platform) => `${BASE}/promos/${id}/click?ref=promo_${id}_${platform}`,
  analyzeDuration: (duration) => request('/promos/analyze-duration', { method: 'POST', body: JSON.stringify({ duration }) }),
  validateQuality: (settings) => request('/promos/validate-quality', { method: 'POST', body: JSON.stringify(settings) }),
  uploadBatch: (files) => request('/promos/upload-batch', { method: 'POST', body: JSON.stringify({ files }) }),
  createBatch: (promos) => request('/promos/create-batch', { method: 'POST', body: JSON.stringify({ promos }) }),
  getBatchStatus: (batchId) => request(`/promos/batch/${batchId}`),
  listBatches: () => request('/promos/batches'),
};

// Users / Follow
export const social = {
  accounts: () => request('/social/accounts'),
  connect: (provider) => request(`/social/${provider}/connect`),
  disconnect: (provider) => request(`/social/${provider}`, { method: 'DELETE' }),
  publish: (promoId, targets, caption) =>
    request('/social/publish', { method: 'POST', body: JSON.stringify({ promoId, targets, caption }) }),
};

export const users = {
  get:       (id) => request(`/users/${id}`),
  search:    (q) => request(`/users/search?q=${encodeURIComponent(q)}`),
  follow:    (id) => request(`/users/${id}/follow`, { method: 'POST' }),
  unfollow:  (id) => request(`/users/${id}/follow`, { method: 'DELETE' }),
  followers: (id) => request(`/users/${id}/followers`),
  following: (id) => request(`/users/${id}/following`),
  feed:      (params = {}) => { const qs = new URLSearchParams(params).toString(); return request(`/feed${qs ? `?${qs}` : ''}`); },
};

// Subscriptions
export const subscriptions = {
  getPlans:      () => request('/subscriptions/plans'),
  checkout:      (tier) => request('/subscriptions/checkout', { method: 'POST', body: JSON.stringify({ tier }) }),
  getCurrent:    () => request('/subscriptions/current'),
  cancel:        () => request('/subscriptions/cancel', { method: 'POST' }),
};

// Bookings
export const bookings = {
  create:        (data) => request('/bookings', { method: 'POST', body: JSON.stringify(data) }),
  list:          (params = {}) => { const qs = new URLSearchParams(params).toString(); return request(`/bookings${qs ? `?${qs}` : ''}`); },
  getCreatorStats: () => request('/bookings/creator-stats'),
  complete:      (id) => request(`/bookings/${id}/complete`, { method: 'POST' }),
};

// Creators
export const creators = {
  getProfile:    (username) => request(`/creators/${username}`),
  getReels:      (username, params = {}) => { const qs = new URLSearchParams(params).toString(); return request(`/creators/${username}/reels${qs ? `?${qs}` : ''}`); },
  setup:         (data) => request('/creators/setup', { method: 'POST', body: JSON.stringify(data) }),
  getFeatured:   (params = {}) => { const qs = new URLSearchParams(params).toString(); return request(`/creators/featured${qs ? `?${qs}` : ''}`); },
};

// Flights (Amadeus-backed search + affiliate booking links)
export const analytics = {
  overview:   ()           => request('/analytics/creator/overview'),
  timeseries: (range='30d')=> request(`/analytics/creator/timeseries?range=${encodeURIComponent(range)}`),
  topPromos:  (limit=10)   => request(`/analytics/creator/top-promos?limit=${limit}`),
  topReels:   (limit=10)   => request(`/analytics/creator/top-reels?limit=${limit}`),
  funnel:     ()           => request('/analytics/creator/funnel'),
  payouts:    ()           => request('/analytics/creator/payouts'),
};

export const admin = {
  check: () => request('/admin/check'),
  overview: (days=30) => request(`/admin/stats/overview?days=${days}`),
  activity: (params={}) => request(`/admin/activity?${new URLSearchParams(params)}`),
  searchUsers: (q='') => request(`/admin/users/search?q=${encodeURIComponent(q)}`),
  userActivity: (id, limit=200) => request(`/admin/users/${id}/activity?limit=${limit}`),
};

export const flights = {
  search:     (params) => request('/flights/search', { method: 'POST', body: JSON.stringify(params) }),
  airports:   (keyword) => request(`/flights/airports?keyword=${encodeURIComponent(keyword)}`),
  getSearch:  (id) => request(`/flights/search/${id}`),
  track:      (data) => request('/flights/track', { method: 'POST', body: JSON.stringify(data) }),
  watch:      (data) => request('/flights/watch', { method: 'POST', body: JSON.stringify(data) }),
  watches:    () => request('/flights/watch'),
  unwatch:    (id) => request(`/flights/watch/${id}`, { method: 'DELETE' }),
  updateWatch: (id, data) => request(`/flights/watch/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
};

// Hotels (partner-backed accommodation search + booking)
export const hotels = {
  search: (params) => request('/hotels/search', { method: 'POST', body: JSON.stringify(params) }),
  book:   (data) => request('/hotels/book', { method: 'POST', body: JSON.stringify(data) }),
};

// Maps (Google Maps-backed geocoding + route optimization, graceful fallback)
export const maps = {
  // Resolve a batch of free-text activity locations to coordinates.
  // locations: [{ id?, name?, location? }]; context disambiguates (e.g. "Rome, Italy").
  geocode: (locations, { context, region } = {}) =>
    request('/maps/geocode', { method: 'POST', body: JSON.stringify({ locations, context, region }) }),
  // Optimize the visiting order of a set of already geocoded points.
  // points: [{ id?, label?, lat, lng }].
  optimize: (points) =>
    request('/maps/optimize', { method: 'POST', body: JSON.stringify({ points }) }),
  // Optimize (and persist) the visiting order of a saved TripDay's activities.
  optimizeTripDay: (tripDayId) =>
    request(`/maps/trip-days/${tripDayId}/optimize`, { method: 'POST' }),
};

// Reviews / ratings
export const feedback = {
  send: (data) => request('/feedback', { method: 'POST', body: JSON.stringify(data) }),
};

export const reviews = {
  listForDestination: (destinationId) => request(`/reviews/destination/${destinationId}`),
  create: (data) => request('/reviews', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => request(`/reviews/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  remove: (id) => request(`/reviews/${id}`, { method: 'DELETE' }),
};

// Trip collaboration: invites, comments, votes
export const collaborators = {
  list:   (tripId)            => request(`/trips/${tripId}/collaborators`),
  invite: (tripId, data)      => request(`/trips/${tripId}/collaborators`, { method: 'POST', body: JSON.stringify(data) }),
  remove: (tripId, collabId)  => request(`/trips/${tripId}/collaborators/${collabId}`, { method: 'DELETE' }),
  accept: (token)             => request('/trips/collaborators/accept', { method: 'POST', body: JSON.stringify({ token }) }),
};

export const tripComments = {
  list:   (tripId, params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/trips/${tripId}/comments${qs ? `?${qs}` : ''}`);
  },
  create: (tripId, data) => request(`/trips/${tripId}/comments`, { method: 'POST', body: JSON.stringify(data) }),
  remove: (tripId, commentId) => request(`/trips/${tripId}/comments/${commentId}`, { method: 'DELETE' }),
};

export const tripVotes = {
  list:   (tripId)            => request(`/trips/${tripId}/votes`),
  upsert: (tripId, data)      => request(`/trips/${tripId}/votes`, { method: 'POST', body: JSON.stringify(data) }),
};

// Recap Reels
export const recapReels = {
  create:     (data) => request('/recap-reels', { method: 'POST', body: JSON.stringify(data) }),
  list:       (params = {}) => { const qs = new URLSearchParams(params).toString(); return request(`/recap-reels${qs ? `?${qs}` : ''}`); },
  get:         (id) => request(`/recap-reels/${id}`),
  publish:    (id) => request(`/recap-reels/${id}/publish`, { method: 'POST' }),
  discard:     (id) => request(`/recap-reels/${id}/discard`, { method: 'POST' }),
  feed:        (params = {}) => { const qs = new URLSearchParams(params).toString(); return request(`/recap-reels/feed${qs ? `?${qs}` : ''}`); },
};

// Feed events (algorithmic ranking engagement tracking)
export const feed = {
  event: (data) => request('/feed/events', { method: 'POST', body: JSON.stringify(data) }),
};

// Partners (affiliate deep-link system)
export const partners = {
  list:      () => request('/partners'),
  deeplink:  (slug, params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/partners/${slug}/deeplink${qs ? `?${qs}` : ''}`);
  },
};

// ── Premium Reels Features API ──────────────────────────────────────────

export const reelEnhancement = {
  getTypes:       () => request('/reels/enhancement/types'),
  enhance:        (promoId, enhancementType, params = {}) =>
    request(`/reels/enhancement/${promoId}/enhance`, { method: 'POST', body: JSON.stringify({ enhancementType, params }) }),
  list:           (promoId) => request(`/reels/enhancement/${promoId}/enhancements`),
  getDefaults:    (promoId, type) => request(`/reels/enhancement/${promoId}/defaults/${type}`),
  generateCaptions: (promoId, language = 'en') =>
    request(`/reels/enhancement/${promoId}/captions`, { method: 'POST', body: JSON.stringify({ language }) }),
  getCaptions:    (promoId) => request(`/reels/enhancement/${promoId}/captions`),
  recommendMusic: (promoId) =>
    request(`/reels/enhancement/${promoId}/music`, { method: 'POST' }),
  saveMusic:      (promoId, data) =>
    request(`/reels/enhancement/${promoId}/music/save`, { method: 'POST', body: JSON.stringify(data) }),
  getMusic:       (promoId) => request(`/reels/enhancement/${promoId}/music`),
  deleteMusic:    (musicId) => request(`/reels/enhancement/music/${musicId}`, { method: 'DELETE' }),
};

export const reelEditing = {
  getPresets:     () => request('/reels/editing/presets'),
  getFilters:     () => request('/reels/editing/filters'),
  getTransitions: () => request('/reels/editing/transitions'),
  createProject:  (promoId, data) =>
    request(`/reels/editing/${promoId}/project`, { method: 'POST', body: JSON.stringify(data) }),
  getProject:     (promoId) => request(`/reels/editing/${promoId}/project`),
  getVersions:    (promoId) => request(`/reels/editing/${promoId}/project/versions`),
  updateProject:  (promoId, versionId, data) =>
    request(`/reels/editing/${promoId}/project/${versionId}`, { method: 'PUT', body: JSON.stringify(data) }),
  applyFilter:    (promoId, filterId) =>
    request(`/reels/editing/${promoId}/apply-filter`, { method: 'POST', body: JSON.stringify({ filterId }) }),
};

export const reelInteraction = {
  interact:     (promoId, interactionType, payload = {}) =>
    request(`/reels/interaction/${promoId}/interact`, { method: 'POST', body: JSON.stringify({ interactionType, payload }) }),
  getInteractions: (promoId, type) => {
    const qs = type ? `?type=${encodeURIComponent(type)}` : '';
    return request(`/reels/interaction/${promoId}/interactions${qs}`);
  },
  createPoll:   (promoId, question, options) =>
    request(`/reels/interaction/${promoId}/poll`, { method: 'POST', body: JSON.stringify({ question, options }) }),
  votePoll:     (promoId, pollId, optionId) =>
    request(`/reels/interaction/${promoId}/poll/${pollId}/vote`, { method: 'POST', body: JSON.stringify({ optionId }) }),
  createDuet:   (promoId, responsePromoId, duetType, layout) =>
    request(`/reels/interaction/${promoId}/duet`, { method: 'POST', body: JSON.stringify({ responsePromoId, duetType, layout }) }),
  getDuets:     (promoId) => request(`/reels/interaction/${promoId}/duets`),
  createCollaborative: (promoId, creatorIds) =>
    request(`/reels/interaction/${promoId}/collaborative`, { method: 'POST', body: JSON.stringify({ creatorIds }) }),
};

export const reelMonetization = {
  sendTip:      (promoId, amountCents, message) =>
    request(`/reels/monetization/${promoId}/tip`, { method: 'POST', body: JSON.stringify({ amountCents, message }) }),
  getTips:      (promoId) => request(`/reels/monetization/${promoId}/tips`),
  addAffiliateLink: (promoId, data) =>
    request(`/reels/monetization/${promoId}/affiliate`, { method: 'POST', body: JSON.stringify(data) }),
  getAffiliateLinks: (promoId) => request(`/reels/monetization/${promoId}/affiliate`),
  trackAffiliateClick: (promoId, linkId) =>
    request(`/reels/monetization/${promoId}/affiliate/${linkId}/click`, { method: 'POST' }),
  setSponsored: (promoId, data) =>
    request(`/reels/monetization/${promoId}/sponsored`, { method: 'POST', body: JSON.stringify(data) }),
  getSponsored: (promoId) => request(`/reels/monetization/${promoId}/sponsored`),
  getBrandDeals:   () => request('/reels/monetization/brand-deals'),
  createBrandDeal: (data) =>
    request('/reels/monetization/brand-deals', { method: 'POST', body: JSON.stringify(data) }),
};

export const reelAnalytics = {
  get:            (promoId) => request(`/reels/analytics/${promoId}`),
  getRetention:   (promoId) => request(`/reels/analytics/${promoId}/retention`),
  getBestTime:    (promoId) => request(`/reels/analytics/${promoId}/best-time`),
  getABTests:     (promoId) => request(`/reels/analytics/${promoId}/ab-tests`),
  createABTest:   (promoId, data) =>
    request(`/reels/analytics/${promoId}/ab-tests`, { method: 'POST', body: JSON.stringify(data) }),
  getCompetitor:  (promoId) => request(`/reels/analytics/${promoId}/competitor`),
};

export const reelDiscovery = {
  getForYou:      (limit, cursor) => {
    const params = new URLSearchParams();
    if (limit) params.set('limit', limit);
    if (cursor != null) params.set('cursor', cursor);
    const qs = params.toString();
    return request(`/reels/discovery/for-you${qs ? `?${qs}` : ''}`);
  },
  getTrendingSounds: () => request('/reels/discovery/trending-sounds'),
  createTrendingSound: (data) =>
    request('/reels/discovery/trending-sounds', { method: 'POST', body: JSON.stringify(data) }),
  trackSoundUse:  (soundId) =>
    request(`/reels/discovery/trending-sounds/${soundId}/use`, { method: 'POST' }),
  getChallenges:  (active) => {
    const qs = active ? '?active=true' : '';
    return request(`/reels/discovery/challenges${qs}`);
  },
  createChallenge: (data) =>
    request('/reels/discovery/challenges', { method: 'POST', body: JSON.stringify(data) }),
  joinChallenge:  (challengeId, promoId) =>
    request(`/reels/discovery/challenges/${challengeId}/join`, { method: 'POST', body: JSON.stringify({ promoId }) }),
  getChallengeEntries: (challengeId) =>
    request(`/reels/discovery/challenges/${challengeId}/entries`),
  voteEntry:      (challengeId, entryId) =>
    request(`/reels/discovery/challenges/${challengeId}/entries/${entryId}/vote`, { method: 'POST' }),
  getLocationReels: (lat, lng, radius) => {
    const qs = new URLSearchParams({ lat, lng, ...(radius ? { radius } : {}) }).toString();
    return request(`/reels/discovery/location?${qs}`);
  },
  getCreatorSpotlight: () => request('/reels/discovery/creator-spotlight'),
};
